import { useState, useRef, useCallback, useEffect } from 'react';
import { userKey } from '../utils/userStorage';
import { preferencesService } from '../services/api';

const AUDIO_ORDER = ['imageOnTop', 'main', 'imageBottom', 'image'];

// Version marker — bump on each fix so we can confirm in console which code is live
const AUDIO_MANAGER_VERSION = 'v2026-05-28-pause-on-scroll';
if (typeof window !== 'undefined' && !window.__audioMgrLogged) {
    console.log('[AudioManager]', AUDIO_MANAGER_VERSION);
    window.__audioMgrLogged = true;
}

// Resolve initial audio pref: server (cross-device) > localStorage (cache) > default
const resolveInitial = (key, initialFromServer, fallback) => {
    if (initialFromServer && initialFromServer[key] !== undefined) {
        return initialFromServer[key];
    }
    try {
        const saved = localStorage.getItem(userKey('nizus_audio_prefs'));
        if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed[key] !== undefined) return parsed[key];
        }
    } catch { /* ignore */ }
    return fallback;
};

const useAudioManager = ({ onChapterEnd, initialAudioPrefs = null } = {}) => {
    // Internal ref for the actual DOM element
    const audioElRef = useRef(null);
    // Tracks when the <audio> element mounts/unmounts in DOM
    const [audioMounted, setAudioMounted] = useState(false);

    // Callback ref: React calls this when <audio> mounts or unmounts.
    // This ensures event listeners get attached even if the element
    // appears after initial render (e.g. after a loading screen).
    const audioRef = useCallback((node) => {
        audioElRef.current = node;
        setAudioMounted(!!node);
    }, []);

    // Playlist
    const [audioList, setAudioList] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(-1);

    // Playback state
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    // Settings (persisted: server > localStorage > defaults)
    const [volume, setVolumeState] = useState(() => resolveInitial('volume', initialAudioPrefs, 1));
    const [playbackRate, setPlaybackRateState] = useState(() => resolveInitial('playbackRate', initialAudioPrefs, 1));
    const [autoNext, setAutoNext] = useState(() => resolveInitial('autoNext', initialAudioPrefs, false));
    const [autoScroll, setAutoScroll] = useState(() => resolveInitial('autoScroll', initialAudioPrefs, false));

    // Re-hydrate from server-side prefs when they arrive after mount (e.g. login flow returns user data async)
    useEffect(() => {
        if (!initialAudioPrefs) return;
        if (initialAudioPrefs.volume !== undefined) setVolumeState(initialAudioPrefs.volume);
        if (initialAudioPrefs.playbackRate !== undefined) setPlaybackRateState(initialAudioPrefs.playbackRate);
        if (initialAudioPrefs.autoNext !== undefined) setAutoNext(initialAudioPrefs.autoNext);
        if (initialAudioPrefs.autoScroll !== undefined) setAutoScroll(initialAudioPrefs.autoScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(initialAudioPrefs)]);

    // Stable refs for event handlers (avoid stale closures)
    const autoNextRef = useRef(autoNext);
    const autoScrollRef = useRef(autoScroll);
    const audioListRef = useRef(audioList);
    const currentIndexRef = useRef(currentIndex);
    const onChapterEndRef = useRef(onChapterEnd);
    const volumeRef = useRef(volume);
    const playbackRateRef = useRef(playbackRate);

    // Flag: true while we are chaining to the next audio.
    // Prevents the spurious 'pause' event (fired by the browser's
    // media-element-load algorithm when audio.src changes) from
    // flipping isPlaying to false between two tracks.
    const chainingRef = useRef(false);

    // Audio autoscroll refs — continuous lerp loop while playing
    const scrollTargetRef = useRef(null);        // null = loop should stop
    const scrollContainerRef = useRef(null);
    const scrollAnimRef = useRef(null);
    const scrollCurrentRef = useRef(0);          // tracked scroll pos (avoids DOM reads in loop)
    // When user scrolls manually, we back off auto-scroll until the next paragraph
    // (otherwise the audio's calculated target would yank them back, causing the
    // "saute en haut" effect when the read paragraph leaves the viewport).
    const userScrollOverrideRef = useRef(false);
    const lastScrollIndexRef = useRef(-1);

    useEffect(() => { autoNextRef.current = autoNext; }, [autoNext]);
    useEffect(() => { autoScrollRef.current = autoScroll; }, [autoScroll]);
    useEffect(() => { audioListRef.current = audioList; }, [audioList]);
    useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
    useEffect(() => { onChapterEndRef.current = onChapterEnd; }, [onChapterEnd]);
    useEffect(() => { volumeRef.current = volume; }, [volume]);
    useEffect(() => { playbackRateRef.current = playbackRate; }, [playbackRate]);

    // Debounced server sync — best-effort, silent failures
    const serverSyncTimeoutRef = useRef(null);
    const syncToServer = useCallback((prefs) => {
        if (serverSyncTimeoutRef.current) clearTimeout(serverSyncTimeoutRef.current);
        serverSyncTimeoutRef.current = setTimeout(() => {
            preferencesService.save({ audioPrefs: prefs }).catch(() => { /* offline / 401 — localStorage still has it */ });
        }, 1000);
    }, []);

    useEffect(() => () => {
        if (serverSyncTimeoutRef.current) clearTimeout(serverSyncTimeoutRef.current);
    }, []);

    // Persist audio settings: write localStorage immediately, sync server in background
    const savePrefs = useCallback((overrides = {}) => {
        let prefs = {};
        try {
            const raw = localStorage.getItem(userKey('nizus_audio_prefs'));
            prefs = raw ? JSON.parse(raw) : {};
            Object.assign(prefs, overrides);
            localStorage.setItem(userKey('nizus_audio_prefs'), JSON.stringify(prefs));
        } catch { /* ignore quota */ }
        syncToServer(prefs);
    }, [syncToServer]);

    // Build audio list from paragraphs data
    const loadChapterAudios = useCallback((paragraphs, chapterAudio, language, autoPlay = false) => {
        const list = [];

        // Chapter title audio first
        if (chapterAudio && chapterAudio[language]) {
            list.push({
                id: `chapter_title`,
                src: chapterAudio[language],
                paragraphId: null,
                type: 'chapterTitle',
            });
        }

        // Paragraph audios in order
        if (paragraphs) {
            paragraphs.forEach(p => {
                const langAudio = p.audio?.[language];
                if (!langAudio) return;

                AUDIO_ORDER.forEach(type => {
                    if (langAudio[type]) {
                        list.push({
                            id: `p_${p.idParagraph}_${type}`,
                            src: langAudio[type],
                            paragraphId: p.idParagraph,
                            type,
                        });
                    }
                });
            });
        }

        setAudioList(list);
        setCurrentTime(0);
        setDuration(0);

        if (autoPlay && list.length > 0) {
            // Auto-play first audio of the new chapter
            setCurrentIndex(0);
            const audio = audioElRef.current;
            if (audio) {
                audio.src = list[0].src;
                audio.volume = volumeRef.current;
                audio.playbackRate = playbackRateRef.current;
                audio.play().catch(() => { setIsPlaying(false); });
            }
        } else {
            setCurrentIndex(-1);
            setIsPlaying(false);
        }
    }, []);

    // Play specific audio by index
    const playByIndex = useCallback((index) => {
        const audio = audioElRef.current;
        const list = audioListRef.current;
        if (!audio || index < 0 || index >= list.length) return;

        const item = list[index];

        // If same index and already loaded, just play/resume
        if (currentIndexRef.current === index && audio.src && audio.src.includes(item.src)) {
            if (audio.paused) {
                audio.play().catch(() => {});
            }
            return;
        }

        // Load new audio
        setCurrentIndex(index);
        audio.src = item.src;
        audio.volume = volumeRef.current;
        audio.playbackRate = playbackRateRef.current;
        audio.play().catch(() => {});
    }, []);

    // Play/Pause toggle
    const playPause = useCallback(() => {
        const audio = audioElRef.current;
        if (!audio) return;

        if (audio.paused) {
            // If nothing loaded yet, start from first
            if (currentIndexRef.current < 0 && audioListRef.current.length > 0) {
                playByIndex(0);
            } else {
                audio.play().catch(() => {});
            }
        } else {
            audio.pause();
        }
    }, [playByIndex]);

    // Stop playback
    const stop = useCallback(() => {
        const audio = audioElRef.current;
        if (!audio) return;

        audio.pause();
        audio.currentTime = 0;
        // Update ref synchronously BEFORE clearing src.
        // Setting audio.src='' triggers a synchronous 'error' event;
        // the handler must see currentIndex=-1 to avoid playing the next track.
        currentIndexRef.current = -1;
        audio.src = '';
        setIsPlaying(false);
        setCurrentIndex(-1);
        setCurrentTime(0);
        setDuration(0);
    }, []);

    // Seek forward/backward
    const seekForward = useCallback(() => {
        const audio = audioElRef.current;
        if (!audio || !audio.duration) return;
        audio.currentTime = Math.min(audio.currentTime + 10, audio.duration);
    }, []);

    const seekBackward = useCallback(() => {
        const audio = audioElRef.current;
        if (!audio) return;
        audio.currentTime = Math.max(audio.currentTime - 10, 0);
    }, []);

    // Volume
    const setVolume = useCallback((v) => {
        const clamped = Math.max(0, Math.min(1, v));
        setVolumeState(clamped);
        if (audioElRef.current) audioElRef.current.volume = clamped;
        savePrefs({ volume: clamped });
    }, [savePrefs]);

    // Playback rate
    const setPlaybackRate = useCallback((rate) => {
        const clamped = Math.max(0.5, Math.min(2, rate));
        setPlaybackRateState(clamped);
        if (audioElRef.current) audioElRef.current.playbackRate = clamped;
        savePrefs({ playbackRate: clamped });
    }, [savePrefs]);

    // Toggles
    const toggleAutoNext = useCallback(() => {
        setAutoNext(prev => {
            savePrefs({ autoNext: !prev });
            return !prev;
        });
    }, [savePrefs]);

    const toggleAutoScroll = useCallback(() => {
        setAutoScroll(prev => {
            savePrefs({ autoScroll: !prev });
            return !prev;
        });
    }, [savePrefs]);

    // Cleanup
    const cleanup = useCallback(() => {
        const audio = audioElRef.current;
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
            // Update refs synchronously before clearing src (same reason as stop())
            currentIndexRef.current = -1;
            audioListRef.current = [];
            audio.src = '';
        }
        setAudioList([]);
        setCurrentIndex(-1);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
    }, []);

    // Audio element event handlers — re-attach when audio element mounts
    useEffect(() => {
        const audio = audioElRef.current;
        if (!audio) return;

        const handlePlay = () => {
            chainingRef.current = false;
            // Re-engage auto-scroll on (re)play — user explicitly asked for playback to continue,
            // so the manual-scroll back-off should be cleared.
            userScrollOverrideRef.current = false;
            setIsPlaying(true);
        };

        const stopScrollLoop = () => {
            scrollTargetRef.current = null;
            if (scrollAnimRef.current) {
                cancelAnimationFrame(scrollAnimRef.current);
                scrollAnimRef.current = null;
            }
        };

        const handlePause = () => {
            // Ignore the spurious 'pause' event fired by the browser
            // when audio.src changes during automatic chaining.
            if (chainingRef.current) return;
            setIsPlaying(false);
            stopScrollLoop();
        };

        const handleEnded = () => {
            const list = audioListRef.current;
            const idx = currentIndexRef.current;

            if (idx >= 0 && idx < list.length - 1) {
                // Chain: play next audio in list
                chainingRef.current = true;
                const nextIdx = idx + 1;
                const nextItem = list[nextIdx];
                const prevItem = list[idx];
                console.log('[AudioManager] chain', {
                    from: { idx, src: prevItem?.src?.split('/').slice(-2).join('/'), paragraphId: prevItem?.paragraphId, type: prevItem?.type },
                    to:   { idx: nextIdx, src: nextItem?.src?.split('/').slice(-2).join('/'), paragraphId: nextItem?.paragraphId, type: nextItem?.type },
                });
                setCurrentIndex(nextIdx);
                audio.src = nextItem.src;
                audio.volume = volumeRef.current;
                audio.playbackRate = playbackRateRef.current;
                // play() forces load + play even with preload="none"
                audio.play().catch(() => {
                    chainingRef.current = false;
                    setIsPlaying(false);
                });
            } else {
                // Playlist finished
                setIsPlaying(false);
                stopScrollLoop();
                if (autoNextRef.current && onChapterEndRef.current) {
                    setTimeout(() => onChapterEndRef.current(), 500);
                }
            }
        };

        const handleError = () => {
            // If an audio file fails to load, skip to the next one.
            // Guard idx >= 0: stop()/cleanup() set currentIndexRef=-1
            // synchronously before clearing audio.src, which fires this handler.
            chainingRef.current = false;
            const list = audioListRef.current;
            const idx = currentIndexRef.current;

            if (idx >= 0 && idx < list.length - 1) {
                const nextIdx = idx + 1;
                const nextItem = list[nextIdx];
                setCurrentIndex(nextIdx);
                audio.src = nextItem.src;
                audio.volume = volumeRef.current;
                audio.playbackRate = playbackRateRef.current;
                audio.play().catch(() => { setIsPlaying(false); });
            } else {
                setIsPlaying(false);
            }
        };

        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime || 0);

            if (!autoScrollRef.current || !audio.duration) return;

            const list = audioListRef.current;
            const idx = currentIndexRef.current;
            if (idx < 0 || idx >= list.length) return;

            // Paragraph just changed → reset user-scroll override (re-engage auto-scroll for the new paragraph)
            const paragraphChanged = idx !== lastScrollIndexRef.current;
            if (paragraphChanged) {
                userScrollOverrideRef.current = false;
                lastScrollIndexRef.current = idx;
            }

            // User scrolled manually since last paragraph → leave them alone
            if (userScrollOverrideRef.current) return;

            const item = list[idx];
            if (!item.paragraphId) return;

            const el = document.querySelector(`[data-paragraph-id="${item.paragraphId}"]`);
            const scrollContainer = el && (el.closest('[data-scroll-container]') || el.closest('.overflow-y-auto'));
            if (!el || !scrollContainer) return;

            const containerRect = scrollContainer.getBoundingClientRect();

            // For image-variant audios (imageOnTop, imageBottom, image), scroll to show the image
            // — but only once when transitioning, then freeze. The audio narrates the image, so we
            // don't progress through text; we just need the image to be in view.
            if (item.type !== 'main') {
                if (paragraphChanged) {
                    const img = el.querySelector('img');
                    if (img) {
                        const imgRect = img.getBoundingClientRect();
                        const imgTopInContainer = scrollContainer.scrollTop + imgRect.top - containerRect.top;
                        // Center the image vertically — but if it's taller than the viewport,
                        // just align its top with a small offset (so user sees the top of the image).
                        let target;
                        if (img.offsetHeight >= scrollContainer.clientHeight - 40) {
                            target = imgTopInContainer - 20; // 20px breathing room at top
                        } else {
                            target = imgTopInContainer - (scrollContainer.clientHeight - img.offsetHeight) / 2;
                        }
                        const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;
                        scrollContainerRef.current = scrollContainer;
                        scrollTargetRef.current = Math.max(0, Math.min(maxScroll, target));
                        console.log('[AudioManager] image-variant scroll', {
                            type: item.type,
                            imgTopInContainer,
                            imgHeight: img.offsetHeight,
                            viewportHeight: scrollContainer.clientHeight,
                            target: scrollTargetRef.current,
                        });
                        // Start lerp loop if not running
                        if (!scrollAnimRef.current) {
                            scrollCurrentRef.current = scrollContainer.scrollTop;
                            const LERP_FACTOR = 0.04;
                            const MAX_DELTA_PX = 4;
                            const loop = () => {
                                const c = scrollContainerRef.current;
                                const t = scrollTargetRef.current;
                                if (c === null || t === null || !autoScrollRef.current || userScrollOverrideRef.current) {
                                    scrollAnimRef.current = null;
                                    return;
                                }
                                if (Math.abs(c.scrollTop - scrollCurrentRef.current) > 8) {
                                    userScrollOverrideRef.current = true;
                                    scrollAnimRef.current = null;
                                    return;
                                }
                                let delta = (t - scrollCurrentRef.current) * LERP_FACTOR;
                                if (delta > MAX_DELTA_PX)  delta = MAX_DELTA_PX;
                                if (delta < -MAX_DELTA_PX) delta = -MAX_DELTA_PX;
                                scrollCurrentRef.current += delta;
                                c.scrollTop = scrollCurrentRef.current;
                                scrollAnimRef.current = requestAnimationFrame(loop);
                            };
                            scrollAnimRef.current = requestAnimationFrame(loop);
                        }
                    }
                }
                // Don't update target on subsequent timeUpdates — let the lerp settle on the image.
                return;
            }

            // Measure the text-only div (excludes images/audio/video media)
            // Falls back to the full paragraph if the inner text div isn't tagged.
            const textEl = el.querySelector(`[data-paragraph-text="${item.paragraphId}"]`) || el;
            const textHeight = Math.max(textEl.offsetHeight, 50);

            // Absolute position of TEXT element in scroll container (use the text div, not the wrapper)
            const elRect = textEl.getBoundingClientRect();
            const elTopInContainer = scrollContainer.scrollTop + elRect.top - containerRect.top;

            if (paragraphChanged) {
                const usingTextEl = textEl !== el;
                const naturalPxSec = audio.duration > 0 ? (textHeight / audio.duration) : 0;
                console.log('[AudioManager] paragraph change', {
                    audioSrc: audio.src?.split('/').slice(-2).join('/'),
                    paragraphId: item.paragraphId,
                    type: item.type,
                    textHeight,
                    fullElHeight: el.offsetHeight,
                    measuredTextEl: usingTextEl,
                    audioDuration: audio.duration,
                    seekableEnd: audio.seekable.length > 0 ? audio.seekable.end(0) : null,
                    playbackRate: audio.playbackRate,
                    naturalPxPerSec: Math.round(naturalPxSec),
                    effectivePxPerSec: Math.round(Math.min(naturalPxSec, 15)),
                    capped: naturalPxSec > 15,
                    elTopInContainer,
                });
            }

            // Cap scroll progress to a comfortable reading speed.
            // If the audio narration is shorter than what it'd take to read the visible text at
            // ~1 line/sec, don't blast through the text — stop the scroll where the audio actually
            // takes us, and let the user read the rest at their own pace.
            const MAX_PX_PER_SEC = 15;
            const naturalPxPerSec = audio.duration > 0 ? (textHeight / audio.duration) : 0;
            const effectivePxPerSec = Math.min(naturalPxPerSec, MAX_PX_PER_SEC);
            const scrolledPx = Math.floor(audio.currentTime * effectivePxPerSec);
            const rawTarget = elTopInContainer - 200 + scrolledPx;
            const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;
            const clampedTarget = Math.max(0, Math.min(maxScroll, rawTarget));

            scrollContainerRef.current = scrollContainer;
            scrollTargetRef.current = clampedTarget;

            // Start continuous lerp loop if not already running.
            // scrollCurrentRef tracks position without reading scrollTop on every frame
            // (DOM writes are cheap; DOM reads force layout flushes).
            if (!scrollAnimRef.current) {
                scrollCurrentRef.current = scrollContainer.scrollTop; // one-time read to init
                const LERP_FACTOR = 0.04;       // 4% per frame — half as aggressive as before
                const MAX_DELTA_PX = 4;          // hard cap on pixels moved per frame (prevents sudden lurches)
                const loop = () => {
                    const c = scrollContainerRef.current;
                    const t = scrollTargetRef.current;
                    if (c === null || t === null || !autoScrollRef.current || userScrollOverrideRef.current) {
                        scrollAnimRef.current = null;
                        return;
                    }
                    // Detect manual user scroll: if the real DOM scrollTop diverges from our
                    // model by more than a small threshold, the user moved the scrollbar.
                    // Pause the audio — when user resumes (handlePlay), the override is cleared
                    // and auto-scroll re-engages from the current position.
                    if (Math.abs(c.scrollTop - scrollCurrentRef.current) > 8) {
                        userScrollOverrideRef.current = true;
                        scrollAnimRef.current = null;
                        const a = audioElRef.current;
                        if (a && !a.paused) a.pause();
                        return;
                    }
                    // Smooth lerp toward target, but cap the per-frame delta to avoid
                    // visible lurches when the target jumps (paragraph transitions, image-load reflows…).
                    let delta = (t - scrollCurrentRef.current) * LERP_FACTOR;
                    if (delta > MAX_DELTA_PX)  delta = MAX_DELTA_PX;
                    if (delta < -MAX_DELTA_PX) delta = -MAX_DELTA_PX;
                    scrollCurrentRef.current += delta;
                    c.scrollTop = scrollCurrentRef.current; // write-only — no layout flush
                    scrollAnimRef.current = requestAnimationFrame(loop);
                };
                scrollAnimRef.current = requestAnimationFrame(loop);
            }
        };

        const handleLoadedMetadata = () => {
            setDuration(audio.duration || 0);
            console.log('[AudioManager] loadedmetadata', {
                src: audio.src?.split('/').pop(),
                duration: audio.duration,
                seekableEnd: audio.seekable.length > 0 ? audio.seekable.end(0) : null,
            });
        };

        const handleDurationChange = () => {
            console.log('[AudioManager] durationchange', {
                src: audio.src?.split('/').pop(),
                newDuration: audio.duration,
            });
        };
        audio.addEventListener('durationchange', handleDurationChange);

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('durationchange', handleDurationChange);
            if (scrollAnimRef.current) {
                cancelAnimationFrame(scrollAnimRef.current);
                scrollAnimRef.current = null;
            }
        };
    }, [audioMounted]);

    // Get first audio index for a given paragraph
    const getFirstIndexForParagraph = useCallback((paragraphId) => {
        return audioList.findIndex(a => a.paragraphId === paragraphId);
    }, [audioList]);

    // Check if current audio belongs to a paragraph
    const isPlayingParagraph = useCallback((paragraphId) => {
        if (currentIndex < 0 || currentIndex >= audioList.length) return false;
        return audioList[currentIndex].paragraphId === paragraphId;
    }, [currentIndex, audioList]);

    // Get an ordered, deduplicated list of paragraphIds present in the playlist.
    // Skip the chapterTitle item (paragraphId === null).
    const getParagraphIds = useCallback(() => {
        const seen = new Set();
        const ids = [];
        audioListRef.current.forEach(a => {
            if (a.paragraphId != null && !seen.has(a.paragraphId)) {
                seen.add(a.paragraphId);
                ids.push(a.paragraphId);
            }
        });
        return ids;
    }, []);

    // Jump to first audio of previous paragraph (relative to current). Wraps to last paragraph if at first.
    const playPrevParagraph = useCallback(() => {
        const list = audioListRef.current;
        if (!list.length) return;
        const ids = getParagraphIds();
        if (!ids.length) return;
        const cur = list[currentIndexRef.current];
        const curId = cur?.paragraphId;
        const curPos = curId != null ? ids.indexOf(curId) : -1;
        const prevId = ids[curPos > 0 ? curPos - 1 : ids.length - 1];
        const targetIdx = list.findIndex(a => a.paragraphId === prevId);
        if (targetIdx >= 0) playByIndex(targetIdx);
    }, [getParagraphIds, playByIndex]);

    // Jump to first audio of next paragraph (relative to current). Wraps to first paragraph if at last.
    const playNextParagraph = useCallback(() => {
        const list = audioListRef.current;
        if (!list.length) return;
        const ids = getParagraphIds();
        if (!ids.length) return;
        const cur = list[currentIndexRef.current];
        const curId = cur?.paragraphId;
        const curPos = curId != null ? ids.indexOf(curId) : -1;
        const nextId = ids[curPos >= 0 && curPos < ids.length - 1 ? curPos + 1 : 0];
        const targetIdx = list.findIndex(a => a.paragraphId === nextId);
        if (targetIdx >= 0) playByIndex(targetIdx);
    }, [getParagraphIds, playByIndex]);

    return {
        audioRef,

        // State
        audioList,
        currentIndex,
        isPlaying,
        currentTime,
        duration,
        volume,
        playbackRate,
        autoNext,
        autoScroll,
        hasAudio: audioList.length > 0,

        // Actions
        loadChapterAudios,
        playByIndex,
        playPause,
        stop,
        seekForward,
        seekBackward,
        playPrevParagraph,
        playNextParagraph,
        setVolume,
        setPlaybackRate,
        toggleAutoNext,
        toggleAutoScroll,
        cleanup,

        // Helpers
        getFirstIndexForParagraph,
        isPlayingParagraph,
    };
};

export default useAudioManager;
